import React from 'react';
import ReactDOM from 'react-dom/client';
import { Router, routeRequiresUIFrame } from './router/routes';
import { PROTECTED_PATHS } from './router/protectedPaths';
import { I18nProvider } from './i18n';
import { ThemeProvider, applyTheme, readStoredTheme } from './theme/theme';
import { initRuntime } from './core/runtime';
import { useAuthGate } from './hooks/useAuthGate';
import { pageWebSocketUrl } from './api/pageWebSocketUrl';
import './index.css';

// Stop the browser's auto-translate from mangling this Chinese UI. With the
// static index.html declaring lang="en", Chrome treats the page as English and
// "translates" it to the user's locale — rewriting ticker symbols
// (AAPL -> 苹果公司, MU -> 亩, ARM -> 手臂) in the orders/supervisor tables and,
// worse, wrapping text nodes so React throws "removeChild ... not a child of
// this node" during commit. Declaring the real language + opting out of
// translation removes the trigger. This lives in committed src (not index.html,
// which is outside the L1 commit allowlist and kept reverting) so it survives
// rebuilds and reverts.
try {
  const html = document.documentElement;
  html.lang = 'zh-CN';
  html.setAttribute('translate', 'no');
  if (!document.querySelector('meta[name="google"][content="notranslate"]')) {
    const meta = document.createElement('meta');
    meta.name = 'google';
    meta.content = 'notranslate';
    document.head.appendChild(meta);
  }
} catch {
  /* non-DOM/SSR safety — no-op */
}

// Initialize runtime lazily; route-level gating keeps pages that do not use
// UIFrame transport (notably /broker) from paying the WebSocket/reconnect cost.
// HTTPS pages (q.2222503.xyz) must use wss://. A hardcoded ws:// URL is
// mixed content and browsers silently block it, so /dashboard-v12 never
// receives UIFrame ranking rows while the cockpit's own wss socket still works.
const WS_URL = pageWebSocketUrl('/ws/ui');
const runtime = initRuntime({ wsUrl: WS_URL });
// R9-ext 2026-06-24: keep runtime reference reachable so Vite/Rollup
// tree-shaking does not eliminate the lazily started runtime.
// Exposed on globalThis for debugging and for the dashboard to read
// the live connection status without going through the WSClient instance.
(globalThis as unknown as { __V12_RUNTIME__: typeof runtime }).__V12_RUNTIME__ = runtime;

// ADR-025 P2 (2026-06-22): AuthGateRoot mounts the useAuthGate hook at
// the top of the React tree. It does NOT touch the Router (per the
// 6/21 ORPHAN decision) and does NOT change what the Router renders —
// it only subscribes to the v12-auth-401 event and the initial
// /api/auth/status probe, redirecting to /login?from=... if the user
// lands on a protected path without a session.
//
// Protected paths include every page that reads the authenticated R10 control
// plane. The SPA shell may still be served anonymously, but its control-plane
// requests must not be left rendering a bare HTTP 401 when the session is
// absent or expires.
function AuthGateRoot({ children }: { children: React.ReactNode }) {
  useAuthGate({ protectedPaths: PROTECTED_PATHS });
  return <>{children}</>;
}

function RuntimeGate({ children }: { children: React.ReactNode }) {
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    const maybeStart = () => {
      if (!startedRef.current && routeRequiresUIFrame(window.location.pathname)) {
        runtime.start();
        startedRef.current = true;
      }
    };

    maybeStart();
    window.addEventListener('popstate', maybeStart);
    window.addEventListener('v14-spa-navigate', maybeStart);
    return () => {
      window.removeEventListener('popstate', maybeStart);
      window.removeEventListener('v14-spa-navigate', maybeStart);
    };
  }, []);

  return <>{children}</>;
}

// Paint the stored theme before React mounts. theme.tsx documents an inline
// boot script in index.html for this, but index.html is outside the L1 commit
// allowlist and kept reverting (same reason the lang/translate fix above lives
// here), so the pre-paint apply belongs in committed src.
try {
  applyTheme(readStoredTheme());
} catch {
  /* non-DOM/SSR safety — no-op */
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <I18nProvider>
      {/* 2026-07-23: ThemeProvider was defined but never mounted anywhere, so
          every useTheme() consumer received the default context whose setTheme
          is `() => {}`. The Login swatches, and any other theme control, were
          inert: no <html class="theme-*">, no localStorage write, no repaint.
          Mounted here beside I18nProvider — the pattern theme.tsx says it
          mirrors — so the choice is genuinely global. */}
      <ThemeProvider>
        <AuthGateRoot>
          <RuntimeGate>
            <Router />
          </RuntimeGate>
        </AuthGateRoot>
      </ThemeProvider>
    </I18nProvider>
  </React.StrictMode>
);
